import { html, LitElement } from "lit";
import { query, state } from 'lit/decorators.js';
import { styleMap } from "lit/directives/style-map.js";
import { msg } from "@lit/localize";
import buttonStyle from '../buttons/common.scss';
import commonStyle from './usos-box.scss';
import '../navigation';

enum ExpandState {
    Expanding, Expanded, Collapsing, Collapsed
}

/**
 * Parent class for box-decorated information.
 *
 * @slot - box content
 * @slot expandable-content - optional extra content hidden by default
 */
export abstract class UsosBox extends LitElement {
    static override styles = [buttonStyle, commonStyle];

    @query('#expandable-content') private _expandContent?: HTMLDivElement;

    @state() hasExpandableContent = false;
    @state() expandState = ExpandState.Collapsed;

    private _isExpanded() {
        return this.hasExpandableContent && [ExpandState.Expanded, ExpandState.Expanding].includes(this.expandState);
    }

    private _getExpandContentHeight() {
        return this._expandContent?.firstElementChild?.clientHeight ?? 0;
    }

    private _transitionEnd() {
        this.expandState = this.expandState == ExpandState.Expanding ? ExpandState.Expanded : ExpandState.Collapsed;
    }

    private _toggleExpand() {
        const oldState = this.expandState;
        if (oldState == ExpandState.Expanded) { // ręczne transition
            this._expandContent!.style.height = `${this._getExpandContentHeight()}px`;
        }
        setTimeout(() => {
            this.expandState = this._isExpanded() ? ExpandState.Collapsing : ExpandState.Expanding;
        }, 50);
    }

    private _onExpandableSlotChange(e: Event) {
        const assignedNodes = (e.target as HTMLSlotElement).assignedNodes();
        this.hasExpandableContent = assignedNodes.length > 0;
        if (this.hasExpandableContent) {
            assignedNodes.filter((node) => {
                return node.nodeType == Node.ELEMENT_NODE;
            }).forEach((node, idx) => {
                (node as Element).classList[idx ? 'remove' : 'add']('first-expandable-content');
            })
        }
    }

    override render() {
        const expandableStyle = {display: !this.hasExpandableContent ? 'none' : null};
        let height = null;
        switch (this.expandState) {
            case ExpandState.Expanding:
                height = `${this._getExpandContentHeight()}px`;
                break;
            case ExpandState.Collapsed:
            case ExpandState.Collapsing:
                height = `0px`;
                break;
            case ExpandState.Expanded:
                height = 'auto';
                break;
        }
        const expandableContentStyle = {height};

        return html`
            <div><div></div></div>
            <div>
                <slot></slot>
                <div id="expandable-content" aria-hidden=${!this._isExpanded()} @slotchange=${this._onExpandableSlotChange} @transitionend=${this._transitionEnd} style=${styleMap(expandableContentStyle)}>
                    <div><div>
                        <slot name='expandable-content'></slot>
                    </div></div>
                </div>
                <div id="expand-control" style=${styleMap(expandableStyle)} class="usos-ui">
                    <button @click=${this._toggleExpand} class="reset"
                        aria-controls="expandable-content" aria-expanded=${this._isExpanded()}>
                        ${this._isExpanded() ? msg('Mniej') : msg('Więcej')}
                        <usos-icon icon-name=${this._isExpanded() ? 'expand_less' : 'expand_more'}></usos-icon>
                    </button>
                </div>
            </div>
        `;
    }
}

